import { useEffect, useRef, useState } from 'react'
import * as s from './cve-browser-facsimile.css.ts'

// ── placeholder bars ────────────────────────────────────────────────────────

const Bar = ({ w = '100%', h = 9 }: { w?: string | number; h?: number }) => (
  <div style={{ height: h, background: '#d1d5db', borderRadius: 4, width: w, flexShrink: 0 }} />
)

const DarkBar = ({ w = '100%', h = 9 }: { w?: string | number; h?: number }) => (
  <div style={{ height: h, background: '#30363d', borderRadius: 4, width: w, flexShrink: 0 }} />
)

// ── ecosystem detection ──────────────────────────────────────────────────────

type Ecosystem = 'npm' | 'maven' | 'pypi' | 'go' | 'rubygems' | 'nuget' | 'cargo'

function detectEcosystem(packageName: string, hint?: string): Ecosystem {
  if (hint) {
    switch (hint) {
      case 'npm':
        return 'npm'
      case 'maven':
        return 'maven'
      case 'pypi':
        return 'pypi'
      case 'go':
        return 'go'
      case 'rubygems':
        return 'rubygems'
      case 'nuget':
        return 'nuget'
      case 'cargo':
        return 'cargo'
    }
  }
  // Maven: groupId:artifactId
  if (packageName.includes(':')) return 'maven'
  // Go: module path (domain/path)
  if (/^[a-z0-9-]+\.[a-z]{2,}\//.test(packageName)) return 'go'
  return 'npm'
}

// ── registry page definitions ────────────────────────────────────────────────

type RegistryPage = {
  host: string
  path: string
  tabLabel: string
  tabFavicon: string
  content: React.ReactNode
  status: string
}

function NpmPage({ packageName }: { packageName: string }) {
  return (
    <div style={{ fontFamily: '-apple-system, sans-serif', background: '#fff', height: '100%', fontSize: 12 }}>
      <div style={{ background: '#cb3837', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ background: '#fff', color: '#cb3837', fontWeight: 900, fontSize: 11, padding: '1px 4px' }}>
          npm
        </span>
        <Bar w={120} h={14} />
      </div>
      <div style={{ padding: '10px 13px' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#333', marginBottom: 4 }}>{packageName}</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: 3,
              padding: '2px 7px',
              fontSize: 10,
              color: '#dc2626',
              fontWeight: 600,
            }}
          >
            ⚠ vulnerability
          </div>
          <Bar w={70} h={20} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 9 }}>
          <Bar w="94%" />
          <Bar w="86%" />
          <Bar w="65%" />
        </div>
        <div style={{ border: '1px solid #eee', borderRadius: 5, overflow: 'hidden' }}>
          {[60, 45, 72, 38].map((w, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '5px 9px',
                background: i % 2 ? '#fafafa' : '#fff',
                borderBottom: i < 3 ? '1px solid #f0f0f0' : 'none',
              }}
            >
              <Bar w={80} h={8} />
              <Bar w={`${w}%`} h={8} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function MavenPage({ packageName }: { packageName: string }) {
  return (
    <div style={{ fontFamily: 'Arial, sans-serif', background: '#fff', height: '100%', fontSize: 12 }}>
      <div
        style={{
          background: '#fff',
          borderBottom: '3px solid #c33',
          padding: '7px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <span style={{ color: '#c33', fontWeight: 900, fontSize: 14, letterSpacing: '-0.02em' }}>Maven</span>
        <span style={{ color: '#666', fontSize: 11 }}>Central Repository</span>
        <div
          style={{
            marginLeft: 'auto',
            background: '#f5f5f5',
            border: '1px solid #ddd',
            borderRadius: 4,
            padding: '3px 9px',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span style={{ fontSize: 10, color: '#999' }}>🔍</span>
          <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#333' }}>{packageName}</span>
        </div>
      </div>
      <div style={{ padding: '10px 13px' }}>
        <div style={{ fontSize: 10, color: '#888', marginBottom: 6 }}>Search Results</div>
        {[1, 2, 3].map(i => (
          <div
            key={i}
            style={{
              border: '1px solid #e8e8e8',
              borderRadius: 5,
              padding: '9px 11px',
              marginBottom: 6,
              background: '#fafafa',
            }}
          >
            <div style={{ fontFamily: 'monospace', color: '#c33', fontSize: 12, marginBottom: 4 }}>{packageName}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Bar w={`${75 - i * 10}%`} h={8} />
              <Bar w={`${60 - i * 5}%`} h={8} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <Bar w={50} h={7} />
              <Bar w={40} h={7} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function PypiPage({ packageName }: { packageName: string }) {
  return (
    <div style={{ fontFamily: '-apple-system, sans-serif', background: '#fff', height: '100%', fontSize: 12 }}>
      <div style={{ background: '#0073b7', padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>PyPI</span>
        <span style={{ color: 'rgba(255,255,255,.7)', fontSize: 10 }}>the Python Package Index</span>
      </div>
      <div
        style={{ background: '#f5f5f5', borderBottom: '1px solid #ddd', padding: '5px 12px', display: 'flex', gap: 12 }}
      >
        {['Browse', 'Search', 'Statistics', 'Help'].map(t => (
          <span key={t} style={{ fontSize: 10, color: '#0073b7' }}>
            {t}
          </span>
        ))}
      </div>
      <div style={{ padding: '10px 13px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>{packageName}</span>
          <Bar w={60} h={14} />
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <div
            style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: 3,
              padding: '2px 7px',
              fontSize: 10,
              color: '#856404',
              fontWeight: 600,
            }}
          >
            ⚠ vulnerability
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 9 }}>
          <Bar w="92%" />
          <Bar w="83%" />
          <Bar w="60%" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {['Author', 'License', 'Requires', 'Classifiers'].map(label => (
            <div
              key={label}
              style={{ background: '#f9f9f9', border: '1px solid #eee', borderRadius: 4, padding: '6px 8px' }}
            >
              <div
                style={{
                  fontSize: 9,
                  color: '#aaa',
                  textTransform: 'uppercase' as const,
                  letterSpacing: '.06em',
                  marginBottom: 3,
                }}
              >
                {label}
              </div>
              <Bar w="65%" h={8} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function GoPage({ packageName }: { packageName: string }) {
  return (
    <div style={{ fontFamily: '-apple-system, sans-serif', background: '#fff', height: '100%', fontSize: 12 }}>
      <div style={{ background: '#00acd7', padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>pkg.go.dev</span>
        <div
          style={{
            flex: 1,
            background: 'rgba(255,255,255,.2)',
            borderRadius: 4,
            padding: '2px 8px',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
        >
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,.7)' }}>🔍</span>
          <span style={{ fontSize: 11, color: '#fff', fontFamily: 'monospace' }}>{packageName}</span>
        </div>
      </div>
      <div style={{ padding: '10px 13px' }}>
        <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#00acd7', marginBottom: 2 }}>{packageName}</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 7 }}>
          <Bar w={55} h={20} />
          <Bar w={45} h={20} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 9 }}>
          <Bar w="93%" />
          <Bar w="80%" />
          <Bar w="63%" />
        </div>
        <div style={{ border: '1px solid #e8e8e8', borderRadius: 5, overflow: 'hidden' }}>
          <div style={{ background: '#f5f5f5', padding: '4px 9px', borderBottom: '1px solid #e8e8e8' }}>
            <Bar w={120} h={8} />
          </div>
          {[55, 70, 48, 62].map((w, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                gap: 10,
                padding: '5px 9px',
                borderBottom: i < 3 ? '1px solid #f0f0f0' : 'none',
              }}
            >
              <Bar w={90} h={8} />
              <Bar w={`${w}%`} h={8} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function RubygemsPage({ packageName }: { packageName: string }) {
  return (
    <div style={{ fontFamily: '-apple-system, sans-serif', background: '#fff', height: '100%', fontSize: 12 }}>
      <div style={{ background: '#e9573f', padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>💎 RubyGems</span>
        <span style={{ color: 'rgba(255,255,255,.7)', fontSize: 10 }}>the Ruby community gem host</span>
      </div>
      <div style={{ padding: '10px 13px' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#333', marginBottom: 4 }}>{packageName}</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: 3,
              padding: '2px 7px',
              fontSize: 10,
              color: '#dc2626',
              fontWeight: 600,
            }}
          >
            ⚠ vulnerability
          </div>
          <Bar w={65} h={20} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 9 }}>
          <Bar w="90%" />
          <Bar w="78%" />
          <Bar w="55%" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {['Downloads', 'Homepage', 'Authors', 'License'].map(label => (
            <div
              key={label}
              style={{ background: '#f9f9f9', border: '1px solid #eee', borderRadius: 4, padding: '6px 8px' }}
            >
              <div
                style={{
                  fontSize: 9,
                  color: '#aaa',
                  textTransform: 'uppercase' as const,
                  letterSpacing: '.06em',
                  marginBottom: 3,
                }}
              >
                {label}
              </div>
              <Bar w="60%" h={8} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function NugetPage({ packageName }: { packageName: string }) {
  return (
    <div style={{ fontFamily: '-apple-system, sans-serif', background: '#fff', height: '100%', fontSize: 12 }}>
      <div style={{ background: '#004880', padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>NuGet</span>
        <span style={{ color: 'rgba(255,255,255,.6)', fontSize: 10 }}>Gallery</span>
      </div>
      <div style={{ padding: '10px 13px' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#333', marginBottom: 4 }}>{packageName}</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: 3,
              padding: '2px 7px',
              fontSize: 10,
              color: '#dc2626',
              fontWeight: 600,
            }}
          >
            ⚠ vulnerability
          </div>
          <Bar w={60} h={20} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 9 }}>
          <Bar w="91%" />
          <Bar w="77%" />
          <Bar w="58%" />
        </div>
        <div style={{ border: '1px solid #e8e8e8', borderRadius: 5, overflow: 'hidden' }}>
          {[
            ['Version', 60],
            ['Downloads', 45],
            ['License', 55],
            ['Authors', 50],
          ].map(([label, w], i) => (
            <div
              key={String(label)}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '5px 9px',
                borderBottom: i < 3 ? '1px solid #f0f0f0' : 'none',
                background: i % 2 ? '#fafafa' : '#fff',
              }}
            >
              <span style={{ fontSize: 10, color: '#888', width: 70 }}>{label}</span>
              <Bar w={`${w}%`} h={8} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function CargoPage({ packageName }: { packageName: string }) {
  return (
    <div style={{ fontFamily: '-apple-system, sans-serif', background: '#fff', height: '100%', fontSize: 12 }}>
      <div style={{ background: '#593d28', padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ color: '#f4c460', fontWeight: 700, fontSize: 13 }}>⚙ crates.io</span>
        <span style={{ color: 'rgba(255,255,255,.6)', fontSize: 10 }}>the Rust community's crate registry</span>
      </div>
      <div style={{ padding: '10px 13px' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#333', marginBottom: 4 }}>{packageName}</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: 3,
              padding: '2px 7px',
              fontSize: 10,
              color: '#dc2626',
              fontWeight: 600,
            }}
          >
            ⚠ vulnerability (RustSec)
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 9 }}>
          <Bar w="89%" />
          <Bar w="74%" />
          <Bar w="52%" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {['Downloads', 'Repository', 'License', 'Editions'].map(label => (
            <div
              key={label}
              style={{ background: '#f9f9f9', border: '1px solid #eee', borderRadius: 4, padding: '6px 8px' }}
            >
              <div
                style={{
                  fontSize: 9,
                  color: '#aaa',
                  textTransform: 'uppercase' as const,
                  letterSpacing: '.06em',
                  marginBottom: 3,
                }}
              >
                {label}
              </div>
              <Bar w="65%" h={8} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── registry page factory ────────────────────────────────────────────────────

function makeRegistryPage(ecosystem: Ecosystem, packageName: string): RegistryPage {
  switch (ecosystem) {
    case 'npm':
      return {
        host: 'npmjs.com',
        // eslint-disable-next-line pixee/no-unescaped-path-params
        path: `/package/${packageName}`,
        tabLabel: `${packageName} - npm`,
        tabFavicon: '⬥',
        status: 'npmjs.com — package',
        content: <NpmPage packageName={packageName} />,
      }
    case 'maven':
      return {
        host: 'search.maven.org',
        path: `/search?q=${encodeURIComponent(packageName)}`,
        tabLabel: `Maven · ${packageName}`,
        tabFavicon: '🏺',
        status: 'search.maven.org — results',
        content: <MavenPage packageName={packageName} />,
      }
    case 'pypi':
      return {
        host: 'pypi.org',
        // eslint-disable-next-line pixee/no-unescaped-path-params
        path: `/project/${packageName}/`,
        tabLabel: `${packageName} · PyPI`,
        tabFavicon: '🐍',
        status: 'pypi.org — project',
        content: <PypiPage packageName={packageName} />,
      }
    case 'go':
      return {
        host: 'pkg.go.dev',
        // eslint-disable-next-line pixee/no-unescaped-path-params
        path: `/${packageName}`,
        tabLabel: `${packageName} - Go Packages`,
        tabFavicon: '🐹',
        status: 'pkg.go.dev — package',
        content: <GoPage packageName={packageName} />,
      }
    case 'rubygems':
      return {
        host: 'rubygems.org',
        // eslint-disable-next-line pixee/no-unescaped-path-params
        path: `/gems/${packageName}`,
        tabLabel: `${packageName} | RubyGems`,
        tabFavicon: '💎',
        status: 'rubygems.org — gem',
        content: <RubygemsPage packageName={packageName} />,
      }
    case 'nuget':
      return {
        host: 'nuget.org',
        // eslint-disable-next-line pixee/no-unescaped-path-params
        path: `/packages/${packageName}`,
        tabLabel: `${packageName} - NuGet`,
        tabFavicon: '📦',
        status: 'nuget.org — package',
        content: <NugetPage packageName={packageName} />,
      }
    case 'cargo':
      return {
        host: 'crates.io',
        // eslint-disable-next-line pixee/no-unescaped-path-params
        path: `/crates/${packageName}`,
        tabLabel: `${packageName} - crates.io`,
        tabFavicon: '⚙',
        status: 'crates.io — crate',
        content: <CargoPage packageName={packageName} />,
      }
  }
}

// ── static pages (always shown) ──────────────────────────────────────────────

function NvdPage({ cveId }: { cveId: string }) {
  return (
    <div style={{ fontFamily: 'Arial, sans-serif', background: '#fff', height: '100%', fontSize: 12 }}>
      <div style={{ background: '#003366', padding: '7px 12px', display: 'flex', alignItems: 'center' }}>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 12, letterSpacing: '.03em' }}>
          NVD · National Vulnerability Database
        </span>
        <span style={{ color: '#8fa8c8', fontSize: 10, marginLeft: 'auto' }}>nist.gov</span>
      </div>
      <div
        style={{
          background: '#e8eef5',
          borderBottom: '1px solid #c8d8e8',
          padding: '4px 12px',
          display: 'flex',
          gap: 14,
        }}
      >
        {['Home', 'Vulnerabilities', 'Statistics', 'Products'].map(t => (
          <span key={t} style={{ fontSize: 10, color: '#336699' }}>
            {t}
          </span>
        ))}
      </div>
      <div style={{ padding: '11px 13px' }}>
        <div style={{ fontSize: 10, color: '#666', marginBottom: 3 }}>Vulnerability Detail</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#003366', marginBottom: 7 }}>{cveId}</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 9 }}>
          <span
            style={{
              background: '#b91c1c',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 3,
            }}
          >
            CRITICAL
          </span>
          <Bar w={55} h={18} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 9 }}>
          <Bar w="96%" />
          <Bar w="88%" />
          <Bar w="71%" />
        </div>
        <div style={{ border: '1px solid #dde', borderRadius: 4, overflow: 'hidden' }}>
          {[42, 65, 55, 48].map((w, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                gap: 8,
                padding: '5px 8px',
                borderBottom: i < 3 ? '1px solid #eee' : 'none',
                background: i % 2 ? '#f8f8fc' : '#fff',
              }}
            >
              <Bar w={75} h={8} />
              <Bar w={`${w}%`} h={8} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function CveOrgPage({ cveId }: { cveId: string }) {
  return (
    <div style={{ fontFamily: '-apple-system, sans-serif', background: '#fff', height: '100%', fontSize: 12 }}>
      <div
        style={{ background: '#cc0000', padding: '0 12px', display: 'flex', alignItems: 'center', height: 36, gap: 8 }}
      >
        <span style={{ color: '#fff', fontWeight: 900, fontSize: 15 }}>CVE</span>
        <span style={{ color: 'rgba(255,255,255,.6)', fontSize: 10 }}>Common Vulnerabilities and Exposures</span>
      </div>
      <div
        style={{ background: '#f5f5f5', borderBottom: '1px solid #ddd', padding: '4px 12px', display: 'flex', gap: 14 }}
      >
        {['Home', 'CVE List', 'Search', 'Downloads'].map(t => (
          <span key={t} style={{ fontSize: 10, color: '#555' }}>
            {t}
          </span>
        ))}
      </div>
      <div style={{ padding: '11px 13px' }}>
        <div style={{ fontSize: 10, color: '#888', marginBottom: 3 }}>CVERecord</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#111', marginBottom: 6 }}>{cveId}</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 9 }}>
          <span
            style={{
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: 4,
              padding: '2px 8px',
              fontSize: 10,
              color: '#dc2626',
              fontWeight: 600,
            }}
          >
            Published
          </span>
          <Bar w={90} h={20} />
        </div>
        <div
          style={{
            background: '#f9f9f9',
            border: '1px solid #e5e5e5',
            borderRadius: 6,
            padding: '9px 11px',
            marginBottom: 9,
          }}
        >
          <div
            style={{
              fontSize: 9,
              color: '#aaa',
              textTransform: 'uppercase' as const,
              letterSpacing: '.06em',
              marginBottom: 5,
            }}
          >
            Description
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <Bar w="97%" />
            <Bar w="90%" />
            <Bar w="62%" />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
          {['CVSS Score', 'CWE', 'References', 'Affected'].map(label => (
            <div
              key={label}
              style={{ background: '#f9f9f9', border: '1px solid #eee', borderRadius: 4, padding: '7px 9px' }}
            >
              <div
                style={{
                  fontSize: 9,
                  color: '#aaa',
                  textTransform: 'uppercase' as const,
                  letterSpacing: '.06em',
                  marginBottom: 4,
                }}
              >
                {label}
              </div>
              <Bar w="70%" h={8} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function GitHubSearchPage({ cveId }: { cveId: string }) {
  return (
    <div
      style={{
        fontFamily: '-apple-system, sans-serif',
        background: '#0d1117',
        height: '100%',
        fontSize: 12,
        color: '#e6edf3',
      }}
    >
      <div
        style={{
          background: '#161b22',
          borderBottom: '1px solid #30363d',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <span style={{ fontSize: 18, lineHeight: 1 }}>⬡</span>
        <div
          style={{
            flex: 1,
            background: '#0d1117',
            border: '1px solid #30363d',
            borderRadius: 6,
            padding: '3px 9px',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span style={{ fontSize: 10, color: '#8b949e' }}>🔍</span>
          <span style={{ fontSize: 11, color: '#e6edf3', fontFamily: 'monospace' }}>{cveId}</span>
        </div>
      </div>
      <div style={{ padding: '8px 13px' }}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 8, borderBottom: '1px solid #21262d', paddingBottom: 7 }}>
          {['Repositories', 'Code', 'Issues', 'Pull requests'].map((t, i) => (
            <span
              key={t}
              style={{
                fontSize: 11,
                color: i === 0 ? '#e6edf3' : '#8b949e',
                borderBottom: i === 0 ? '2px solid #f78166' : 'none',
                paddingBottom: 7,
              }}
            >
              {t}
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[80, 55, 70].map((w, i) => (
            <div
              key={i}
              style={{ background: '#161b22', border: '1px solid #21262d', borderRadius: 6, padding: '9px 11px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                <span style={{ fontSize: 10 }}>📦</span>
                <DarkBar w={`${w}%`} h={9} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 5 }}>
                <DarkBar w="90%" h={7} />
                <DarkBar w="68%" h={7} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <DarkBar w={38} h={7} />
                <DarkBar w={28} h={7} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── main component ───────────────────────────────────────────────────────────

type PageDef = {
  host: string
  path: string
  tabLabel: string
  tabFavicon: string
  status: string
  content: React.ReactNode
}

function buildPages(cveId: string, packageName: string | undefined, ecosystem: Ecosystem): PageDef[] {
  const pages: PageDef[] = [
    {
      host: 'nvd.nist.gov',
      // eslint-disable-next-line pixee/no-unescaped-path-params
      path: `/vuln/detail/${cveId}`,
      tabLabel: `${cveId} - NVD`,
      tabFavicon: '🏛',
      status: 'nvd.nist.gov — document loaded',
      content: <NvdPage cveId={cveId} />,
    },
    {
      host: 'www.cve.org',
      path: `/CVERecord?id=${cveId}`,
      tabLabel: `CVERecord · ${cveId}`,
      tabFavicon: '🔴',
      status: 'cve.org — document loaded',
      content: <CveOrgPage cveId={cveId} />,
    },
    {
      host: 'github.com',
      path: `/search?q=${cveId}&type=repositories`,
      tabLabel: `${cveId} · GitHub`,
      tabFavicon: '⬡',
      status: 'github.com — done',
      content: <GitHubSearchPage cveId={cveId} />,
    },
  ]
  if (packageName) {
    pages.push(makeRegistryPage(ecosystem, packageName))
  }
  return pages
}

const CYCLE_MS = 2800
const TRANSITION_MS = 220

type Props = {
  cveId: string
  packageName?: string
  ecosystem?: string
}

export const CveBrowserFacsimile: React.FC<Props> = ({ cveId, packageName, ecosystem: ecosystemHint }) => {
  const [current, setCurrent] = useState(0)
  const [loading, setLoading] = useState(false)
  const [visible, setVisible] = useState(true)
  const [done, setDone] = useState(false)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const cycleCountRef = useRef(0)

  const ecosystem = detectEcosystem(packageName ?? '', ecosystemHint)
  const pages = buildPages(cveId, packageName, ecosystem)
  const PAGE_COUNT = pages.length

  useEffect(() => {
    setCurrent(0)
    setVisible(true)
    setLoading(false)
    setDone(false)
    cycleCountRef.current = 0
  }, [cveId, packageName])

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    cycleCountRef.current = 0
    const advance = () => {
      cycleCountRef.current += 1
      if (cycleCountRef.current >= PAGE_COUNT) {
        if (timerRef.current) clearInterval(timerRef.current)
        setLoading(false)
        setVisible(false)
        setDone(true)
        return
      }
      setCurrent(c => (c + 1) % PAGE_COUNT)
      setLoading(false)
      setVisible(true)
    }
    timerRef.current = setInterval(() => {
      setLoading(true)
      setVisible(false)
      setTimeout(advance, TRANSITION_MS)
    }, CYCLE_MS)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [PAGE_COUNT])

  if (done) return null

  const p = pages[current] as PageDef
  const nextP = pages[(current + 1) % PAGE_COUNT] as PageDef

  return (
    <div className={s.browser}>
      <div className={s.titlebar}>
        <div className={s.traffic}>
          <span className={s.dot({ color: 'red' })} />
          <span className={s.dot({ color: 'yellow' })} />
          <span className={s.dot({ color: 'green' })} />
        </div>
        <div className={s.tabs}>
          <div className={s.tab({ active: true })}>
            <span className={s.tabFavicon}>{p.tabFavicon}</span>
            <span className={s.tabTitle}>{p.tabLabel}</span>
          </div>
          <div className={s.tab({ active: false })}>
            <span className={s.tabFavicon}>{nextP.tabFavicon}</span>
            <span className={s.tabTitle}>{nextP.tabLabel}</span>
          </div>
        </div>
      </div>

      <div className={s.toolbar}>
        <button className={s.navBtn}>←</button>
        <button className={s.navBtn}>→</button>
        <button className={s.navBtn}>↻</button>
        <div className={s.urlbar}>
          <span className={s.urlLock}>🔒</span>
          <div className={s.urlText}>
            <span className={s.urlHost}>{p.host}</span>
            <span className={s.urlPathPart}>{p.path}</span>
          </div>
          <div className={s.spinner({ spinning: loading })} />
        </div>
      </div>

      <div className={s.viewport}>
        {pages.map((pg, i) => (
          <div key={i} className={s.page({ visible: i === current && visible })}>
            {pg.content}
          </div>
        ))}
      </div>

      <div className={s.statusbar}>
        <div className={s.statusDot} />
        <div className={s.statusText}>{loading ? `Connecting to ${nextP.host}…` : p.status}</div>
      </div>
    </div>
  )
}
